import React from 'react';
import { Box } from '@mui/material';
import { summaryRowSx } from './panelSurfaces';

/**
 * Formats the stats and futureDates for the Messages Panel tabs.
 * Returns an array: [SummaryTabContent, FutureDatesTabContent, Reserved1, Reserved2]
 *
 * @param {Array<string>} stats - List of summary/stat lines from backend
 * @param {Array<string>} futureDates - List of future date lines from backend (optional)
 * @returns {Array<React.ReactNode>} tabContents
 */
export function formatMessagesPanelTabs(stats = [], futureDates = []) {
  console.log('[formatMessagesPanelTabs] called with:', { stats, futureDates });
  console.log('[formatMessagesPanelTabs] stats values:', stats);
  console.log('[formatMessagesPanelTabs] futureDates values:', futureDates);
  console.log('[formatMessagesPanelTabs] called with:', { stats, futureDates });
  // Use the arrays as provided: summaryLines = stats, futureDatesLines = futureDates
  let summaryLines = stats;
  let futureDatesLines = futureDates;

  // Extract Long Side Gains and Short Side Gains for Net Gain computation
  let longSideGains = null;
  let shortSideGains = null;
  summaryLines.forEach(line => {
    if (typeof line !== 'string') return;
    const lm = line.match(/^Long Side Gains:\s*([\d.-]+)$/);
    if (lm) longSideGains = parseFloat(lm[1]);
    const sm = line.match(/^Short Side Gains:\s*([\d.-]+)$/);
    if (sm) shortSideGains = parseFloat(sm[1]);
  });

  /*
   * The row style now comes from `panelSurfaces`, which is a THEME CALLBACK rather than a colour.
   *
   * These rows set a background and never set a text colour, so they inherited one -- dark while
   * the only theme was light, and light the moment the dark theme shipped. Measured at that point:
   * 1.11:1 on the green pill, 1.02:1 on the red, 1.10:1 on the yellow. Invisible, with nothing to
   * error about, because a colour is never wrong -- only unreadable.
   */
  const summaryRows = [];
  let rowIdx = 0;
  summaryLines.forEach((line, idx) => {
    // Bold all numbers after a colon in any stat line
    // Safety check: Ensure 'line' is a string before calling .match()
    const match = typeof line === 'string' ? line.match(/^(.*?:)\s*([\d.-]+)$/) : null;
    const variant = rowIdx % 2 === 0 ? 'positive' : 'negative';
    if (match) {
      const label = match[1].toLowerCase();
      const isCount = label.includes('# of');
      const displayNum = isCount ? String(Math.round(parseFloat(match[2]))) : parseFloat(match[2]).toFixed(2);
      summaryRows.push(
        <Box key={idx} sx={summaryRowSx(variant)}>
          <span>{match[1]}</span>
          <Box component="span" sx={{ fontWeight: 'bold', textAlign: 'right', minWidth: 50, fontFamily: 'monospace' }}>{displayNum}</Box>
        </Box>
      );
    } else {
      summaryRows.push(
        <Box key={idx} sx={summaryRowSx(variant)}>{line}</Box>
      );
    }
    rowIdx++;

    // Insert Net Gain row right after Short Side Gains
    if (typeof line === 'string' && line.startsWith('Short Side Gains:') && longSideGains !== null && shortSideGains !== null) {
      const netGainNum = (longSideGains - shortSideGains).toFixed(2);
      summaryRows.push(
        <Box key={`net-gain-${idx}`} sx={(theme) => ({
          // The highlight variant carries its own gradient and its heavier border, so the two
          // are declared beside the colours they belong to rather than overridden here. Spread
          // from a theme callback, which is why this sx is one too.
          ...summaryRowSx('highlight')(theme),
          '@keyframes flashNum': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.3 },
          },
        })}>
          <span style={{ fontWeight: 'bold' }}>Net Gain (longside - shortside):</span>
          <Box component="span" sx={{
            fontWeight: 'bold', textAlign: 'right', minWidth: 50, fontFamily: 'monospace',
            animation: 'flashNum 1.2s ease-in-out infinite',
          }}>{netGainNum}</Box>
        </Box>
      );
      rowIdx++;
    }
  });
  const summaryTab = <Box>{summaryRows}</Box>;

  /*
   * The Future Dates tab is the SAME pills with the same defect, and is fixed with it.
   *
   * It was not in the report -- that named the Summary tab -- but it is the identical construct
   * three lines down in the same file, so leaving it would have made the dialog readable on one
   * tab and blank on the next.
   *
   * <p>The separator's grey was `#888`, which is a mid-grey chosen against a pastel. An OPACITY
   * instead, so it is dimmer than its own row's text in whichever theme is running rather than a
   * fixed colour that is only dim against one of them.
   */
  const cellSx = (idx) => (theme) => ({
    ...summaryRowSx(idx % 2 === 0 ? 'positive' : 'negative')(theme),
    display: 'block',
  });

  // Future Dates tab: each line formatted as yyyy-MM-dd -to- yyyy-MM-dd
  const futureTab = (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
      {futureDatesLines.map((line, idx) => {
        // Try to parse date1-to-date2
        let date1 = '', date2 = '';
        // Safety check: Ensure 'line' is a string before calling .match()
        const match = typeof line === 'string' ? line.match(/^(\d{4}-\d{2}-\d{2})[- ]*to[- ]*(\d{4}-\d{2}-\d{2})$/) : null;
        if (match) {
          date1 = match[1];
          date2 = match[2];
        } else {
          // fallback: show raw line
          return (
            <Box key={idx} sx={[cellSx(idx), { gridColumn: 'span 3' }]}>{line}</Box>
          );
        }
        return [
          <Box key={idx + '-date1'} sx={[cellSx(idx), { textAlign: 'right' }]}>{date1}</Box>,
          <Box key={idx + '-sep'} sx={[cellSx(idx), { textAlign: 'center', opacity: 0.7 }]}>to</Box>,
          <Box key={idx + '-date2'} sx={[cellSx(idx), { textAlign: 'left' }]}>{date2}</Box>
        ];
      })}
    </Box>
  );

  // Reserved tabs (empty for now)
  // `text.disabled` rather than `#aaa`: the same intent -- a placeholder, visibly dimmer than
  // real content -- expressed as the token the theme already has for it, so it stays dim rather
  // than becoming a mid-grey on a mid-dark surface.
  const reservedSx = { px: 2, py: 1, color: 'text.disabled' };
  const reserved1 = <Box sx={reservedSx}>Reserved</Box>;
  const reserved2 = <Box sx={reservedSx}>Reserved</Box>;

  console.log('[formatMessagesPanelTabs] returns:', { summaryTab, futureTab, reserved1, reserved2 });
  return [summaryTab, futureTab, reserved1, reserved2];
}
